import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CalendarDays, ChevronDown, Clock, CreditCard, Landmark, MapPin, Smartphone, Sprout, UserRound, Wallet, X } from "lucide-react";
import {
  defaultActivity,
  formatActivityCurrency,
  formatActivityDate,
  getTotalAmount,
  packageOptions,
} from "../Confirm activity/Confirm.js";
import "../../Holidays/Confirm booking/Confirm.css";
import "./Payment.css";

const paymentModes = [
  { id: "UPI", description: "Make Online Payments Directly from Bank", Icon: Smartphone },
  { id: "Credit/Debit/ATM Cards", description: "Use VISA, Mastercard, American Express etc.", Icon: CreditCard },
  { id: "Wallets", description: "Choose Mobikwik, Payzapp, PhonePe or Amazon", Icon: Wallet },
  { id: "Net Banking", description: "All Major banks are supported", Icon: Landmark },
  { id: "EMI", description: "HSBC, RBI, ICICI, Yes and others bank for EMI", Icon: CreditCard },
  { id: "Google Pay", description: "Pay Easily with Google Pay", Icon: Smartphone, isGooglePay: true },
];

const walletOptions = [
  { name: "MobiKwik", icon: "MK", className: "mobikwik" },
  { name: "PayZapp", icon: "PZ", className: "payzapp" },
  { name: "PhonePe", icon: "PP", className: "phonepe" },
  { name: "Amazon", icon: "A", className: "amazon" },
];

const bankOptions = [
  { name: "ICICI Bank", icon: "IC", className: "icici" },
  { name: "Punjab National Bank", icon: "PN", className: "pnb" },
  { name: "State Bank of India", icon: "SBI", className: "sbi" },
  { name: "Yes Bank", icon: "YES", className: "yes" },
  { name: "HDFC Bank", icon: "H", className: "hdfc" },
  { name: "Axis Bank", icon: "AX", className: "axis" },
];

const emiOptions = [
  {
    type: "Credit Card",
    banks: [
      { name: "IDFC FIRST Bank", icon: "F", tenures: [9, 12], rate: 0.16 },
      { name: "HDFC Bank", icon: "H", tenures: [3, 6, 9, 12], rate: 0.16 },
      { name: "ICICI Bank", icon: "IC", tenures: [3, 6, 9, 12], rate: 0.15 },
      { name: "Axis Bank", icon: "AX", tenures: [6, 9, 12], rate: 0.15 },
    ],
  },
  {
    type: "No Cost EMI",
    banks: [
      { name: "IDFC FIRST Bank", icon: "F", tenures: [3, 6, 9], rate: 0 },
      { name: "HDFC Bank", icon: "H", tenures: [3, 6, 9, 12], rate: 0 },
      { name: "ICICI Bank", icon: "IC", tenures: [3, 6], rate: 0 },
    ],
  },
];

function formatEmiCurrency(value) {
  const amountValue = Number(value || 0);
  const hasDecimal = Math.round(amountValue * 100) % 100 !== 0;
  return `Rs ${amountValue.toLocaleString("en-IN", {
    minimumFractionDigits: hasDecimal ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

export default function ActivityPayment(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const paymentState = location.state || {};
  const activity = props.activity || paymentState.activity || defaultActivity;
  const packageName = props.packageName || paymentState.packageName || packageOptions[0].title;
  const date = props.date || paymentState.date || activity.date || defaultActivity.date;
  const time = props.time || paymentState.time || activity.time || defaultActivity.time;
  const travelers = Number(props.travelers || paymentState.travelers || activity.travelers || defaultActivity.travelers);
  const amount = Number(props.amount || paymentState.amount || getTotalAmount(packageOptions[0], travelers));
  const [contact, setContact] = useState({
    email: paymentState.contact?.email || "",
    phone: paymentState.contact?.phone || "",
  });
  const [travellerProofs, setTravellerProofs] = useState(() => (
    Array.from({ length: Math.max(1, travelers) }, (_, index) => ({
      nationality: paymentState.travellerProofs?.[index]?.nationality || "India",
      name: paymentState.travellerProofs?.[index]?.name || "",
      panNumber: paymentState.travellerProofs?.[index]?.panNumber || "",
      dob: paymentState.travellerProofs?.[index]?.dob || "",
    }))
  ));
  const [formTouched, setFormTouched] = useState(false);
  const [pickupRequired, setPickupRequired] = useState(false);
  const [selectedDonation, setSelectedDonation] = useState(10);
  const [gstOpen, setGstOpen] = useState(false);
  const [agreed, setAgreed] = useState(true);
  const [showPayment, setShowPayment] = useState(false);
  const [timer, setTimer] = useState(9 * 60 + 2);
  const [paymentMode, setPaymentMode] = useState("UPI");
  const [upi, setUpi] = useState({ id: "", provider: "" });
  const [googlePay, setGooglePay] = useState({ id: "", provider: "" });
  const [cardPayment, setCardPayment] = useState({ number: "", validThrough: "", cvv: "", holderName: "" });
  const [selectedWallet, setSelectedWallet] = useState("");
  const [selectedBank, setSelectedBank] = useState("");
  const [emiType, setEmiType] = useState("Credit Card");
  const [selectedEmiBank, setSelectedEmiBank] = useState("IDFC FIRST Bank");
  const [selectedEmiTenure, setSelectedEmiTenure] = useState(9);
  const [emiOpen, setEmiOpen] = useState({ bank: true, tenure: false, card: false });
  const [emiCard, setEmiCard] = useState({ number: "", holderName: "", month: "", year: "", cvv: "" });

  const taxes = 0;
  const grandTotal = Number(amount || 0) + taxes;
  const displayTime = String(time || "").replace(" PM", "").replace(" AM", "");
  const timerText = `${String(Math.floor(timer / 60)).padStart(2, "0")}:${String(timer % 60).padStart(2, "0")}`;
  const isContactValid = contact.email.trim() && contact.phone.trim();
  const areProofsValid = travellerProofs.every((proof) => (
    proof.name.trim() && proof.panNumber.trim() && proof.dob
  ));
  const canProceed = Boolean(agreed && isContactValid && areProofsValid);
  const activeEmiType = emiOptions.find((option) => option.type === emiType) || emiOptions[0];
  const activeEmiBank = activeEmiType.banks.find((bank) => bank.name === selectedEmiBank) || activeEmiType.banks[0];
  const emiPlans = activeEmiBank.tenures.map((tenure) => {
    const interest = (grandTotal * activeEmiBank.rate * tenure) / 12;
    const total = grandTotal + interest;
    return {
      tenure,
      principal: grandTotal,
      interest,
      monthly: total / tenure,
    };
  });
  const selectedEmiPlan = emiPlans.find((plan) => plan.tenure === selectedEmiTenure) || emiPlans[0];
  const isEmiCardReady = Boolean(
    emiCard.number.trim()
    && emiCard.holderName.trim()
    && emiCard.month
    && emiCard.year
    && emiCard.cvv.trim()
  );

  useEffect(() => {
    if (!showPayment) return undefined;
    const interval = setInterval(() => {
      setTimer((current) => Math.max(0, current - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [showPayment]);

  const handleContinue = () => {
    setFormTouched(true);
    if (!canProceed) return;
    setTimer(9 * 60 + 2);
    setShowPayment(true);
  };

  const updateTravellerProof = (index, key, value) => {
    setTravellerProofs((current) => current.map((proof, proofIndex) => (
      proofIndex === index ? { ...proof, [key]: value } : proof
    )));
  };

  const handleEmiTypeChange = (type) => {
    const nextType = emiOptions.find((option) => option.type === type) || emiOptions[0];
    setEmiType(nextType.type);
    setSelectedEmiBank(nextType.banks[0].name);
    setSelectedEmiTenure(nextType.banks[0].tenures[0]);
    setEmiOpen({ bank: true, tenure: false, card: false });
  };

  const handleEmiBankChange = (bankName) => {
    const nextBank = activeEmiType.banks.find((bank) => bank.name === bankName) || activeEmiType.banks[0];
    setSelectedEmiBank(nextBank.name);
    setSelectedEmiTenure(nextBank.tenures[0]);
    setEmiOpen({ bank: true, tenure: true, card: false });
  };

  const handleEmiTenureChange = (tenure) => {
    setSelectedEmiTenure(tenure);
    setEmiOpen({ bank: true, tenure: true, card: true });
  };

  const handleReviewBack = () => {
    if (props.onClose) {
      props.onClose();
      return;
    }

    navigate("/activities/confirm", { state: { activity } });
  };

  return (
    <main className="activity-payment-page">
      <section className="activity-payment-shell">
        <nav className="activity-payment-steps" aria-label="Booking steps">
          <button type="button" onClick={handleReviewBack}>1. Review and Travellers</button>
          <span />
          <b>2. Payment</b>
        </nav>

        <div className="activity-payment-layout">
          <section className="activity-payment-main">
            <article className="activity-review-card">
              <img src={activity?.image} alt={activity?.title || packageName} />
              <div className="activity-review-content">
                <h1>{activity?.title || packageName}</h1>
                <div className="activity-review-meta">
                  <span><MapPin size={17} /> {activity?.location || "New Delhi"}</span>
                  <span><Clock size={17} /> Duration : 4 to 5 hours</span>
                </div>
                <div className="activity-review-divider" />
                <div className="activity-review-meta activity-review-schedule">
                  <span><CalendarDays size={16} /> {formatActivityDate(date)}</span>
                  <span><Clock size={16} /> {displayTime}</span>
                  <span><UserRound size={17} /> Adult : {travelers}</span>
                </div>
              </div>
            </article>

            <section className="payment-detail-card">
              <h2>Contact Details</h2>
              <p>Booking details will be shared on the respective mobile number and Email ID.</p>
              <div className="payment-contact-grid">
                <label>
                  Email Address
                  <input
                    type="email"
                    value={contact.email}
                    placeholder="Enter Email Address"
                    onChange={(event) => setContact((current) => ({ ...current, email: event.target.value }))}
                  />
                  {formTouched && !contact.email.trim() && <em className="payment-error-text">Enter Email Address</em>}
                </label>
                <label>
                  Phone Number
                  <div className="payment-phone-field">
                    <span>+91</span>
                    <input
                      type="tel"
                      value={contact.phone}
                      placeholder="Enter Mobile Number"
                      onChange={(event) => setContact((current) => ({ ...current, phone: event.target.value }))}
                    />
                  </div>
                  {formTouched && !contact.phone.trim() && <em className="payment-error-text">Enter Phone Number</em>}
                </label>
              </div>
            </section>

            <section className="payment-detail-card">
              <h2>ID Proof (PAN/Passport Details)</h2>
              <p>Customer's PAN number (Indian) / Passport number (Other Country) mandatory.</p>
              {travellerProofs.map((proof, index) => (
                <div className="payment-proof-block" key={`proof-${index + 1}`}>
                  <h3>Adult {index + 1}</h3>
                  <div className="payment-id-grid">
                    <label>
                      Nationality
                      <select value={proof.nationality} onChange={(event) => updateTravellerProof(index, "nationality", event.target.value)}>
                        <option>India</option>
                        <option>Other Country</option>
                      </select>
                    </label>
                    <label>
                      Name
                      <input
                        type="text"
                        value={proof.name}
                        placeholder="Enter Name"
                        onChange={(event) => updateTravellerProof(index, "name", event.target.value)}
                      />
                      {formTouched && !proof.name.trim() && <em className="payment-error-text">Enter Name</em>}
                    </label>
                    <label className={formTouched && !proof.panNumber.trim() ? "has-error" : ""}>
                      PAN Number
                      <span className="payment-input-wrap">
                        <input
                          type="text"
                          value={proof.panNumber}
                          placeholder="Enter PAN No."
                          onChange={(event) => updateTravellerProof(index, "panNumber", event.target.value)}
                        />
                        {formTouched && !proof.panNumber.trim() && <b><X size={15} /></b>}
                      </span>
                      {formTouched && !proof.panNumber.trim() && <em>Enter Pan Number</em>}
                    </label>
                    <label>
                      Date Of Birth
                      <input
                        type="date"
                        value={proof.dob}
                        onChange={(event) => updateTravellerProof(index, "dob", event.target.value)}
                      />
                      {formTouched && !proof.dob && <em className="payment-error-text">Enter Date Of Birth</em>}
                    </label>
                  </div>
                </div>
              ))}
            </section>

            <section className="payment-detail-card pickup-card">
              <h2>Pickup Location</h2>
              <p>Tell us where you'd like to be picked up from. If you're not sure, you can decide later.</p>
              <label className="pickup-option">
                <input
                  type="checkbox"
                  checked={pickupRequired}
                  onChange={(event) => setPickupRequired(event.target.checked)}
                />
                I'd like to be picked up
              </label>
            </section>

            <section className="donation-card">
              <div>
                <h2>Help us preserve India's Heritage & Green Spaces!</h2>
                <p>
                  Support the EaseMyTrip Foundation to maintain greenery & cleanliness across parks and ASI
                  monuments for future generations. <button type="button">Know More</button>
                </p>
                <div className="donation-options">
                  {[10, 20, 50, 100].map((value) => (
                    <button
                      type="button"
                      key={value}
                      className={selectedDonation === value ? "is-selected" : ""}
                      onClick={() => setSelectedDonation(value)}
                    >
                      {formatActivityCurrency(value)}
                    </button>
                  ))}
                </div>
              </div>
              <span className="donation-illustration" aria-hidden="true">
                <Sprout size={46} />
              </span>
            </section>

            <button type="button" className="gst-toggle" onClick={() => setGstOpen((current) => !current)}>
              <span><input type="checkbox" checked={gstOpen} readOnly /> GST (Optional)</span>
              <ChevronDown size={24} className={gstOpen ? "is-open" : ""} />
            </button>

            {gstOpen && (
              <section className="payment-detail-card gst-fields">
                <label>
                  GST Number
                  <input type="text" placeholder="Enter GST number" />
                </label>
              </section>
            )}

            <label className="payment-agreement">
              <input type="checkbox" checked={agreed} onChange={(event) => setAgreed(event.target.checked)} />
              <span>
                I understand and agree to the rules of this fare, and the <a href="/#" onClick={(event) => event.preventDefault()}>Terms & Conditions</a> of EaseMyTrips
              </span>
            </label>

          </section>

          <aside className="activity-payment-side">
            <section className="price-summary-card">
              <h2>Price Summary</h2>
              <div className="price-row">
                <span>Adult X {travelers}</span>
                <strong>{formatActivityCurrency(amount)}</strong>
              </div>
              <div className="price-row">
                <span>Taxes & Fees</span>
                <strong>+ {formatActivityCurrency(taxes)}</strong>
              </div>
              <div className="price-row grand">
                <span>Grand Total</span>
                <strong>{formatActivityCurrency(grandTotal)}</strong>
              </div>
            </section>

            <button type="button" className="confirm-booking-btn" onClick={handleContinue}>
              Confirm Booking
            </button>
          </aside>
        </div>
      </section>

      {showPayment && (
        <div className="confirm-payment-overlay" onClick={(event) => event.target === event.currentTarget && setShowPayment(false)}>
          <section className="confirm-payment-modal" role="dialog" aria-modal="true" aria-label="Payment mode">
            <button className="confirm-payment-close" type="button" onClick={() => setShowPayment(false)}>x</button>
            <div className="confirm-payment-head">
              <h2>Payment Mode</h2>
              <span>The session will expire in:<b>{timerText}</b></span>
            </div>

            <div className="confirm-payment-box">
              <label className="mobile-payment-mode-select">
                Payment Option
                <select value={paymentMode} onChange={(event) => setPaymentMode(event.target.value)}>
                  {paymentModes.map(({ id }) => <option value={id} key={id}>{id}</option>)}
                </select>
              </label>
              <aside className="confirm-payment-tabs">
                {paymentModes.map(({ id, description, Icon, isGooglePay }) => (
                  <button
                    className={paymentMode === id ? "is-active" : ""}
                    type="button"
                    key={id}
                    onClick={() => setPaymentMode(id)}
                  >
                    <span className={`payment-method-icon ${isGooglePay ? "google-pay-tab-icon" : ""}`}>
                      {isGooglePay ? <b>G</b> : <Icon size={22} />}
                    </span>
                    <strong>{id}</strong>
                    <small>{description}</small>
                  </button>
                ))}
              </aside>

              {paymentMode === "Credit/Debit/ATM Cards" ? (
                <div className="confirm-card-panel">
                  <label>Enter Your Card No.</label>
                  <input
                    value={cardPayment.number}
                    onChange={(event) => setCardPayment((current) => ({ ...current, number: event.target.value }))}
                    placeholder="ENTER CARD NUMBER"
                  />
                  <div className="confirm-card-row">
                    <label>
                      Valid Through (MM/YY)
                      <span>
                        <input
                          value={cardPayment.validThrough}
                          onChange={(event) => setCardPayment((current) => ({ ...current, validThrough: event.target.value }))}
                          placeholder="MM/YY"
                        />
                        <b>i</b>
                      </span>
                    </label>
                    <label>
                      CVV
                      <span>
                        <input
                          value={cardPayment.cvv}
                          onChange={(event) => setCardPayment((current) => ({ ...current, cvv: event.target.value }))}
                          placeholder="CVV"
                        />
                        <b>i</b>
                      </span>
                    </label>
                  </div>
                  <label>Enter Card Holder Name.</label>
                  <input
                    value={cardPayment.holderName}
                    onChange={(event) => setCardPayment((current) => ({ ...current, holderName: event.target.value }))}
                    placeholder="Name On The Card"
                  />
                  <div className="confirm-card-pay-row">
                    <div className="confirm-total-fare">
                      <span>Total Fare:</span>
                      <strong>{formatActivityCurrency(grandTotal)}</strong>
                    </div>
                    <button type="button">Verify & Pay</button>
                  </div>
                  <p className="confirm-secure-note">We use 128-bit secure encryption providing you a SAFE payment environment</p>
                </div>
              ) : paymentMode === "Wallets" ? (
                <div className="confirm-wallet-panel">
                  <h3>Select Your Wallet</h3>
                  <div className="confirm-wallet-grid">
                    {walletOptions.map((wallet) => (
                      <label className={selectedWallet === wallet.name ? "is-active" : ""} key={wallet.name}>
                        <input
                          type="radio"
                          name="activity-wallet"
                          checked={selectedWallet === wallet.name}
                          onChange={() => setSelectedWallet(wallet.name)}
                        />
                        <span className={`wallet-logo ${wallet.className}`}>{wallet.icon}</span>
                        {wallet.name}
                      </label>
                    ))}
                  </div>
                  <div className="confirm-card-pay-row">
                    <div className="confirm-total-fare">
                      <span>Total Fare:</span>
                      <strong>{formatActivityCurrency(grandTotal)}</strong>
                    </div>
                    <button type="button">Verify & Pay</button>
                  </div>
                </div>
              ) : paymentMode === "Net Banking" ? (
                <div className="confirm-bank-panel">
                  <h3>Select Popular Banks</h3>
                  <div className="confirm-bank-grid">
                    {bankOptions.map((bank) => (
                      <label className={selectedBank === bank.name ? "is-active" : ""} key={bank.name}>
                        <input
                          type="radio"
                          name="activity-bank"
                          checked={selectedBank === bank.name}
                          onChange={() => setSelectedBank(bank.name)}
                        />
                        <span className={`bank-logo ${bank.className}`}>{bank.icon}</span>
                        {bank.name}
                      </label>
                    ))}
                  </div>
                  <div className="confirm-card-pay-row">
                    <div className="confirm-total-fare">
                      <span>Total Fare:</span>
                      <strong>{formatActivityCurrency(grandTotal)}</strong>
                    </div>
                    <button type="button">Verify & Pay</button>
                  </div>
                </div>
              ) : paymentMode === "EMI" ? (
                <div className="confirm-emi-panel activity-emi-panel">
                  <h3>Select Banks</h3>
                  <div className="activity-emi-tabs">
                    {emiOptions.map(({ type }) => (
                      <button
                        type="button"
                        className={emiType === type ? "is-active" : ""}
                        onClick={() => handleEmiTypeChange(type)}
                        key={type}
                      >
                        {type}
                      </button>
                    ))}
                  </div>

                  <section className={`activity-emi-step ${emiOpen.bank ? "is-open" : ""}`}>
                    <button
                      type="button"
                      className="activity-emi-step-head"
                      aria-expanded={emiOpen.bank}
                      onClick={() => setEmiOpen((current) => ({ ...current, bank: !current.bank }))}
                    >
                      <span className={selectedEmiBank ? "is-done" : ""}>1</span>
                      <strong>Choose your Bank</strong>
                      <small>You must have its debit/credit card</small>
                      <ChevronDown size={20} />
                    </button>
                    {emiOpen.bank && (
                      <div className="activity-emi-bank-list">
                        {activeEmiType.banks.map((bank) => (
                          <label className="activity-emi-bank-option" key={bank.name}>
                            <input
                              type="radio"
                              name="activity-emi-bank"
                              checked={selectedEmiBank === bank.name}
                              onChange={() => handleEmiBankChange(bank.name)}
                            />
                            <span className="activity-idfc-logo">{bank.icon}</span>
                            {bank.name}
                          </label>
                        ))}
                      </div>
                    )}
                  </section>

                  <section className={`activity-emi-step ${emiOpen.tenure ? "is-open" : ""}`}>
                    <button
                      type="button"
                      className="activity-emi-step-head"
                      aria-expanded={emiOpen.tenure}
                      onClick={() => setEmiOpen((current) => ({ ...current, tenure: !current.tenure }))}
                    >
                      <span className={emiOpen.tenure ? "is-done" : ""}>2</span>
                      <strong>Choose EMI Tenure</strong>
                      <small>Check interest rates and monthly installment beforehand</small>
                      <ChevronDown size={20} />
                    </button>
                    {emiOpen.tenure && (
                      <table className="activity-emi-table">
                        <thead>
                          <tr>
                            <th></th>
                            <th>Tenure</th>
                            <th>Principal Amount</th>
                            <th>Effective Interest*</th>
                            <th>Monthly Installments</th>
                            <th>Effective Interest paid to bank</th>
                          </tr>
                        </thead>
                        <tbody>
                          {emiPlans.map((plan) => (
                            <tr key={plan.tenure}>
                              <td>
                                <input
                                  type="radio"
                                  name="activity-emi-tenure"
                                  checked={selectedEmiTenure === plan.tenure}
                                  onChange={() => handleEmiTenureChange(plan.tenure)}
                                />
                              </td>
                              <td>{plan.tenure}</td>
                              <td>{formatEmiCurrency(plan.principal)}</td>
                              <td>{Math.round(activeEmiBank.rate * 100)}%(p.a.)</td>
                              <td>{formatEmiCurrency(plan.monthly)}</td>
                              <td>{formatEmiCurrency(plan.interest)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </section>

                  <section className={`activity-emi-step ${emiOpen.card ? "is-open" : ""}`}>
                    <button
                      type="button"
                      className="activity-emi-step-head"
                      aria-expanded={emiOpen.card}
                      onClick={() => setEmiOpen((current) => ({ ...current, card: !current.card }))}
                    >
                      <span className={emiOpen.card ? "is-done" : ""}>3</span>
                      <strong>Enter Card Details</strong>
                      <small>Fill up correct details of your debit/credit card</small>
                      <ChevronDown size={20} />
                    </button>
                    {emiOpen.card && (
                      <div className="activity-emi-card-form">
                        <label>
                          Enter Your Card No.
                          <input
                            value={emiCard.number}
                            onChange={(event) => setEmiCard((current) => ({ ...current, number: event.target.value }))}
                            placeholder="xxxx-xxxx-xxxx-xxxx"
                          />
                        </label>
                        <label>
                          Enter Card Holder Name.
                          <input
                            value={emiCard.holderName}
                            onChange={(event) => setEmiCard((current) => ({ ...current, holderName: event.target.value }))}
                            placeholder="Card Holder Name"
                          />
                        </label>
                        <div className="activity-emi-card-row">
                          <label>
                            Expiry Date
                            <select value={emiCard.month} onChange={(event) => setEmiCard((current) => ({ ...current, month: event.target.value }))}>
                              <option value="">Month</option>
                              {["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"].map((month) => <option key={month}>{month}</option>)}
                            </select>
                          </label>
                          <label>
                            <span>&nbsp;</span>
                            <select value={emiCard.year} onChange={(event) => setEmiCard((current) => ({ ...current, year: event.target.value }))}>
                              <option value="">Year</option>
                              {["2026", "2027", "2028", "2029", "2030"].map((year) => <option key={year}>{year}</option>)}
                            </select>
                          </label>
                          <label>
                            CVV
                            <input
                              value={emiCard.cvv}
                              onChange={(event) => setEmiCard((current) => ({ ...current, cvv: event.target.value }))}
                              placeholder="CVV"
                            />
                          </label>
                        </div>
                        <div className="activity-emi-pay-row">
                          <div className="confirm-total-fare">
                            <span>Total Fare:</span>
                            <strong>{formatActivityCurrency(grandTotal)}</strong>
                          </div>
                          <button type="button" disabled={!isEmiCardReady}>Make Payment</button>
                        </div>
                        <p className="confirm-secure-note">We use 128-bit secure encryption providing you a SAFE payment environment</p>
                        <p className="activity-emi-plan-note">
                          {selectedEmiBank} {emiType}: {selectedEmiPlan.tenure} months at {formatEmiCurrency(selectedEmiPlan.monthly)} per month
                        </p>
                      </div>
                    )}
                  </section>
                </div>
              ) : paymentMode === "Google Pay" ? (
                <div className="confirm-google-pay-panel">
                  <div className="google-pay-brand" aria-label="Google Pay">
                    <span className="google-pay-logo-line">
                      <span className="google-g">G</span>
                      <strong>Pay</strong>
                    </span>
                    <small>Money made simple, by Google</small>
                  </div>

                  <label>Enter Your Google Pay ID</label>
                  <div className="google-pay-row">
                    <input
                      value={googlePay.id}
                      onChange={(event) => setGooglePay((current) => ({ ...current, id: event.target.value }))}
                      placeholder="Enter Your UPI ID"
                    />
                    <b>@</b>
                    <select value={googlePay.provider} onChange={(event) => setGooglePay((current) => ({ ...current, provider: event.target.value }))}>
                      <option value="">Select</option>
                      <option>okaxis</option>
                      <option>okhdfcbank</option>
                      <option>oksbi</option>
                      <option>okicici</option>
                    </select>
                  </div>

                  <div className="google-pay-footer">
                    <div className="confirm-total-fare">
                      <span>Total Fare:</span>
                      <strong>{formatActivityCurrency(grandTotal)}</strong>
                    </div>
                    <button type="button">Make Payment</button>
                  </div>

                  <p>
                    By Continuing, you agree to the Rules, <b>Privacy Policy</b>, <b>User Agreement</b> and <b>Terms & Conditions</b> of EaseMyTrip
                  </p>
                </div>
              ) : (
                <div className="confirm-upi-panel">
                  <label>Enter Your UPI ID</label>
                  <div className="confirm-upi-row">
                    <input
                      value={upi.id}
                      onChange={(event) => setUpi((current) => ({ ...current, id: event.target.value }))}
                      placeholder="ex - mobilenumberupi"
                    />
                    <b>@</b>
                    <select value={upi.provider} onChange={(event) => setUpi((current) => ({ ...current, provider: event.target.value }))}>
                      <option value="">select</option>
                      <option>upi</option>
                      <option>paytm</option>
                      <option>okhdfcbank</option>
                    </select>
                  </div>
                  <div className="upi-pay-footer">
                    <div className="confirm-total-fare">
                      <span>Total Fare:</span>
                      <strong>{formatActivityCurrency(grandTotal)}</strong>
                    </div>
                    <button type="button">Verify & Pay</button>
                  </div>
                  <div className="confirm-pay-steps">
                    <b>How it works?</b>
                    <span>Enter registered VPA</span>
                    <span>Check payment request</span>
                    <span>Approve payment</span>
                  </div>
                  <p>
                    By Continuing, you agree to the Rules, <b>Privacy Policy</b>, <b>User Agreement</b> and <b>Terms & Conditions</b> of EaseMyTrip
                  </p>
                </div>
              )}
            </div>
          </section>
        </div>
      )}
    </main>
  );
}
